import type {
  GenerateDesignTokensPaths,
  LooseAutocomplete,
  Spacing,
} from "@biom3/design-tokens";
import {
  type AllDualVariantIconKeys,
  Body,
  Box,
  ButtCon,
  Caption,
  CountdownTimer,
  type MakeResponsive,
  type MakeValidSxValue,
  Stack,
  type SxProps,
} from "@biom3/react";

export interface RedemptionInfoProps {
  type: "stats" | "countdown";
  icon: "Countdown" | "People";
  title: string;
  value?: string;
  countdownValue?: Date;
  onCountdownEnd?: () => void;
  sx?: SxProps;
}

export const RedemptionCountdown = ({
  countdownValue,
  onCountdownEnd,
  size = ["small", "medium", "medium", "large"],
  gap,
  hideSeconds = false,
}: {
  countdownValue: Date;
  size?: MakeResponsive<"small" | "medium" | "large" | "xLarge"> | undefined;
  onCountdownEnd?: () => void;
  gap?:
    | MakeValidSxValue<
        LooseAutocomplete<
          NonNullable<
            GenerateDesignTokensPaths<Spacing, "base", "base.spacing">
          >
        >
      >
    | undefined;
  hideSeconds?: boolean;
}) => {
  return (
    <CountdownTimer
      testId="countdown-timer"
      size={size}
      gap={gap}
      hideSeconds={hideSeconds}
      variant="inverse"
      futureTarget={countdownValue}
      onCountdownEnd={onCountdownEnd}
      justifyContent={"flex-start"}
    />
  );
};

export const RedemptionInfo = ({
  type,
  icon,
  title,
  value,
  countdownValue,
  onCountdownEnd,
  sx,
}: RedemptionInfoProps) => {
  return (
    <Box
      sx={{
        p: "base.spacing.x4",
        bg: "base.color.translucent.emphasis.100",
        borderRadius: "base.borderRadius.x6",
        ...sx,
        width: {
          default: "100%",
          large: "auto",
        },
        alignItems: "center",
      }}
    >
      <Stack
        direction="column"
        alignItems={{
          default: "center",
          large: "flex-start",
        }}
      >
        <Caption sx={{ color: "base.color.text.body.secondary" }}>
          {title}
        </Caption>
        {type === "stats" ? (
          <Stack direction="row" sx={{ display: "flex" }}>
            <ButtCon
              size={["small", "medium", "medium", "large"]}
              variant="primary/inverse"
              icon={icon as AllDualVariantIconKeys}
              disabled
            />
            <Body
              weight="bold"
              sx={{
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {value}
            </Body>
          </Stack>
        ) : countdownValue && countdownValue.getTime() > Date.now() ? (
          <RedemptionCountdown
            countdownValue={countdownValue}
            onCountdownEnd={onCountdownEnd}
          />
        ) : (
          <RedemptionCountdown countdownValue={new Date()} />
        )}
      </Stack>
    </Box>
  );
};
